import { useState, useEffect } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Plus, Trash2, CheckCircle2 } from 'lucide-react'

type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'multi_select' | 'currency' | 'attachment'

type Field = {
  id: string
  field_type: FieldType
  label: string
  identifier: string
  required: boolean
  encrypted: boolean
  description?: string
  position: number
  options: { id: string; label: string; value: string; position: number }[]
  config: Record<string, unknown>
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  field: Field | null
  onSave: (data: {
    field_type: FieldType
    label: string
    required: boolean
    encrypted: boolean
    description: string | null
    placeholder: string | null
  }) => Promise<{ ok: boolean; errors?: string[] }>
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'currency', label: 'Currency' },
  { value: 'attachment', label: 'Attachment' },
]

export function FieldFormSheet({ open, onOpenChange, field, onSave }: Props) {
  const isEditing = !!field
  const [fieldType, setFieldType] = useState<FieldType>('text')
  const [label, setLabel] = useState('')
  const [required, setRequired] = useState(false)
  const [encrypted, setEncrypted] = useState(false)
  const [description, setDescription] = useState('')
  const [placeholder, setPlaceholder] = useState('')
  const [options, setOptions] = useState<{ label: string; value: string }[]>([])
  const [saving, setSaving] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (open) {
      if (field) {
        setFieldType(field.field_type)
        setLabel(field.label)
        setRequired(field.required)
        setEncrypted(field.encrypted)
        setDescription(field.description || '')
        setPlaceholder((field.config?.placeholder as string) || '')
        setOptions(field.options.map(o => ({ label: o.label, value: o.value })))
      } else {
        setFieldType('text')
        setLabel('')
        setRequired(false)
        setEncrypted(false)
        setDescription('')
        setPlaceholder('')
        setOptions([])
      }
      setApiError(null)
      setSuccess(false)
      setSaving(false)
    }
  }, [open, field])

  function handleClose(value: boolean) {
    if (!value) {
      setSuccess(false)
      setApiError(null)
    }
    onOpenChange(value)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!label.trim()) return
    setSaving(true)
    setApiError(null)

    const result = await onSave({
      field_type: fieldType,
      label,
      required,
      encrypted,
      description: description || null,
      placeholder: placeholder || null,
    })

    setSaving(false)

    if (result.ok) {
      setSuccess(true)
      setTimeout(() => handleClose(false), 1000)
    } else {
      setApiError(result.errors?.join(', ') || 'Something went wrong')
    }
  }

  if (success) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="right" className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {isEditing ? 'Field updated' : 'Field created'}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Field' : 'Add Field'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? `Update the "${field!.label}" field configuration`
              : 'Configure a new application field'}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5 px-4">
          {apiError && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg border border-destructive/20">
              {apiError}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="field_type">Field Type</Label>
            <select
              id="field_type"
              className="flex h-9 w-full items-center rounded-md border border-input bg-transparent pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={fieldType}
              onChange={e => setFieldType(e.target.value as FieldType)}
              disabled={isEditing}
            >
              {FIELD_TYPES.map(ft => (
                <option key={ft.value} value={ft.value}>{ft.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Business Name"
              required
              autoFocus
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <Checkbox checked={required} onCheckedChange={c => setRequired(!!c)} />
              <span className="text-sm">Required</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={encrypted} onCheckedChange={c => setEncrypted(!!c)} />
              <span className="text-sm">Encrypted</span>
            </label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea
              id="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Help text shown to applicants"
              rows={2}
            />
          </div>

          {['text', 'textarea', 'email', 'phone'].includes(fieldType) && (
            <div className="space-y-2">
              <Label htmlFor="placeholder">Placeholder <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="placeholder"
                value={placeholder}
                onChange={e => setPlaceholder(e.target.value)}
                placeholder="Input placeholder text"
              />
            </div>
          )}

          {['select', 'multi_select'].includes(fieldType) && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Options</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOptions([...options, { label: '', value: '' }])}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Option
                </Button>
              </div>
              {options.length === 0 && (
                <p className="text-sm text-muted-foreground">No options yet. Add at least one.</p>
              )}
              {options.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    className="flex-1"
                    placeholder="Label"
                    value={opt.label}
                    onChange={e => {
                      const next = [...options]
                      next[i] = { ...next[i], label: e.target.value }
                      setOptions(next)
                    }}
                  />
                  <Input
                    className="flex-1"
                    placeholder="Value"
                    value={opt.value}
                    onChange={e => {
                      const next = [...options]
                      next[i] = { ...next[i], value: e.target.value }
                      setOptions(next)
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setOptions(options.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-4">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={saving || !label.trim()}>
                {saving ? 'Saving...' : isEditing ? 'Update Field' : 'Create Field'}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
